import * as IoC from 'typescript-ioc';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { v4 as uuid } from 'uuid';
import { FormFieldProps, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { action, observable } from 'mobx';
import { ConnectionTemplateConnectionGroupFieldKey, connectionTemplateConnectionGroupFormConfigList } from './connection-template-connection-group-form.config';
import { IConnectionTemplateConnectionGroupDTO, TConnection } from '../../../../../../api/blueprints/connection.template.types';
import { ConnectionTemplatesFormVM } from '../../connection-template-form.viewmodel';
import { EventName, IEventDispatcher } from '../../../../../../infrastructure/event-dispatcher';
import { ConnectionGroupsPassportPreviewViewModel } from '../../../../../_events/_manage-event/shared/connection-groups-list/connection-groups-passport-preview/connection-group-passport-preview.viewmodel';
import { PassportConfigurationMode } from '../../../../../_events/_manage-event/shared/configure-passport-display/configure-passport-display.config';


export class ConnectionTemplateConnectionGroupFormVM {
    private static MAX_CONNECTIONS_IN_GROUP: number = 2;

    private static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'ConnectionTemplateConnectionGroupFormVM', connectionTemplateConnectionGroupFormConfigList);
            }
        }();
    }

    @observable
    private readonly formDataCtrl: AbstractFormViewModelController;
    @observable
    private enabledConnections: TConnection[] = [];
    @observable
    private disabledConnections: TConnection[] = [];
    @observable
    private loading = false;

    constructor(
        public readonly templateformVM: ConnectionTemplatesFormVM,
        public readonly group: IConnectionTemplateConnectionGroupDTO,
        private readonly closeGroupIdToEditPopup: (meetingConnectionId: string) => void,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly eventDispatcher: IEventDispatcher = IoC.Container.get(IEventDispatcher),
    ) {
        this.formDataCtrl = ConnectionTemplateConnectionGroupFormVM.createFormVM(group);
        this.initializeConnectionLists(templateformVM.connections);
        this.registerHandlers();
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get maxEnabledConnections(): number {
        return ConnectionTemplateConnectionGroupFormVM.MAX_CONNECTIONS_IN_GROUP;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get formVM(): AbstractFormViewModelController {
        return this.formDataCtrl;
    }

    public get connectionTemplatesFormVm(): ConnectionTemplatesFormVM {
        return this.templateformVM;
    }
    public onUnmount(): void {
        this.eventDispatcher.unregisterHandler(EventName.ConnectionCreated, this.onConnectionAddedHandler);
        this.eventDispatcher.unregisterHandler(EventName.ConnectionUpdated, this.onConnectionUpdatedHandler);
        this.eventDispatcher.unregisterHandler(EventName.ConnectionDeleted, this.onConnectionDeletedHandler);
    }

    public getFormFieldPropsByKey(key: string): FormFieldProps {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key)
        };
    }

    public getEnabledConnections(): TConnection[] {
        return this.enabledConnections;
    }

    public getDisabledConnections(): TConnection[] {
        return this.disabledConnections;
    }

    public getConnectionGroupsPassportPreviewViewModel = (): ConnectionGroupsPassportPreviewViewModel => {
        const passportPreviewContextParams = {
            meetingId: +this?.group?.meetingConnectionId || null
        };
        const label = (this.formDataCtrl.getValue(ConnectionTemplateConnectionGroupFieldKey.LABEL) as string).trim();
        return new ConnectionGroupsPassportPreviewViewModel(
            passportPreviewContextParams,
            this.enabledConnections,
            label,
            PassportConfigurationMode.ConnectionTemplate
        );
    };

    @action.bound
    public setEnabledConnections(connections: TConnection[]): void {
        this.enabledConnections = connections;
    }

    @action.bound
    public enableConnection(index: number): void {
        const [ connection ] = this.disabledConnections.splice(index, 1);
        this.enabledConnections.push(connection);
    }

    @action.bound
    public disableConnection(index: number): void {
        const [ connection ] = this.enabledConnections.splice(index, 1);
        this.disabledConnections.push(connection);
    }

    @action.bound
    public submitForm(): boolean {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            return;
        }
        const dto = this.toDTO();
        if (this.isEdit()) {
            this.connectionTemplatesFormVm.editConnectionGroup(dto);
        } else {
            this.connectionTemplatesFormVm.addConnectionGroup(dto);

        }
        return true;
    }

    public isEdit = (): boolean => {
        return !!this.group;
    };

    public onDeleteGroupAction = (groupId: string): void => {
        this.connectionTemplatesFormVm.deleteConnectionGroup(groupId);
        this.closeGroupIdToEditPopup(groupId);
    };

    @action
    private initializeConnectionLists(connections: TConnection[]): void {
        this.enabledConnections = this.composeEnabledConnectionsList(connections);
        this.disabledConnections = this.composeDisabledConnectionsList(connections);
    }

    private toDTO(): IConnectionTemplateConnectionGroupDTO {
        const dto = { ...this.group } as IConnectionTemplateConnectionGroupDTO;

        dto.label = (this.formDataCtrl.getValue(ConnectionTemplateConnectionGroupFieldKey.LABEL) as string).trim();

        dto.connections = this.enabledConnections.map((connection, idx) => {
            return {
                // @ts-ignore
                connectionId: connection.customConnectionId ? connection.customConnectionId : connection.connectionId,
                order: idx + 1
            };
        });
        dto.meetingConnectionId = dto?.meetingConnectionId ?? uuid();
        dto.connectionGroupId = dto?.connectionGroupId ?? uuid();
        dto.isDefaultConnection = dto?.isDefaultConnection ?? false;

        return dto;
    }

    private composeEnabledConnectionsList(connections: TConnection[]): TConnection[] {
        if (!this.group || !this.group.connections.length) return [];

        return this.group.connections
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(({ connectionId }) => {
                return connections.find(c => this.connectionTemplatesFormVm.findCurrentConnection(c, connectionId));
            })
            .filter(Boolean)
        ;
    }

    private composeDisabledConnectionsList(connections: TConnection[]): TConnection[] {
        if (!this.group || !this.group.connections.length) return [ ...connections ];
        // @ts-ignore
        return connections.filter(connection => {
            return !this.group.connections.find(c => this.connectionTemplatesFormVm.findConnectionInGroup(c, connection));
        });
    }
    @action.bound
    private onConnectionAddedHandler(connection: TConnection): void {
        this.disabledConnections.push(connection);
    }

    @action.bound
    private onConnectionUpdatedHandler(connection: TConnection): void {
        const idx = this.enabledConnections.concat(this.disabledConnections)
            .findIndex(({ connectionId }) => connection.connectionId === connectionId);

        if (idx === -1) return;

        if (idx < this.enabledConnections.length) {
            this.enabledConnections[idx] = connection;
        } else {
            this.disabledConnections[idx - this.enabledConnections.length] = connection;
        }
    }

    @action.bound
    private onConnectionDeletedHandler(connection: Partial<TConnection>): void {

        const idx = this.enabledConnections.concat(this.disabledConnections)
            .findIndex(({ connectionId }) => connection.connectionId === connectionId);

        if (idx === -1) return;

        if (idx < this.enabledConnections.length) {
            this.enabledConnections.splice(idx, 1);
        } else {
            this.disabledConnections.splice(idx - this.enabledConnections.length, 1);
        }
    }

    private registerHandlers(): void {
        this.eventDispatcher.registerHandler(EventName.ConnectionCreated, this.onConnectionAddedHandler);
        this.eventDispatcher.registerHandler(EventName.ConnectionUpdated, this.onConnectionUpdatedHandler);
        this.eventDispatcher.registerHandler(EventName.ConnectionDeleted, this.onConnectionDeletedHandler);
    }
}
